/**
 * Board component using react-chessboard.
 * Wraps the library with the BoardAdapter interface for future replacement.
 */

import { useState, useMemo } from 'react';
import { Chessboard } from 'react-chessboard';
import type { Square, Piece } from 'react-chessboard/dist/chessboard/types';
import { ArrowsLayer } from './ArrowsLayer';
import { createEngine } from '@daily-puzzle/rules';
import './Board.css';

export interface BoardProps {
    /** Current position in FEN format */
    fen: string;
    /** Board orientation */
    orientation: 'white' | 'black';
    /** Callback when a move is made */
    onMove: (from: string, to: string, promotion?: string) => void;
    /** Last move to highlight */
    lastMove?: { from: string; to: string };
    /** Legal moves from each square (for hints) */
    legalMoves?: Map<string, string[]>;
    /** Whether the board is disabled (can't make moves) */
    disabled?: boolean;
    /** Arrows to draw on the board (start -> end squares) */
    arrows?: string[][];
    /** Squares to highlight for instructional focus */
    highlightSquares?: string[];
    /** Visual intensity for instructional annotations */
    annotationTone?: 'default' | 'subtle';
    /** Callback when any square is tapped (for tap_square/place_piece interactions) */
    onSquareTap?: (square: string) => void;
}

export function Board({
    fen,
    orientation,
    onMove,
    lastMove,
    legalMoves,
    disabled = false,
    arrows,
    highlightSquares,
    annotationTone = 'default',
    onSquareTap,
}: BoardProps) {
    // Track selected square for click-to-move
    const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
    // Track pending promotion move squares (when promotion dialog is shown)
    const [pendingPromotion, setPendingPromotion] = useState<{ from: Square; to: Square } | null>(null);

    // Only allow dragging pieces of the player's color
    const isDraggablePiece = ({ piece }: { piece: Piece; sourceSquare: Square }) => {
        if (disabled) return false;
        // Piece format is like 'wP', 'bK' - first char is color
        const pieceColor = piece[0] === 'w' ? 'white' : 'black';
        return pieceColor === orientation;
    };

    // Handle piece drop from react-chessboard
    const onDrop = (sourceSquare: Square, targetSquare: Square, piece: string): boolean => {
        if (disabled) return false;

        // Clear selection after drag
        setSelectedSquare(null);

        // Check if this is a promotion move (pawn to last rank)
        // piece format is like 'wP' or 'bP'
        const isPawn = piece.toLowerCase().endsWith('p');
        const isWhitePromotion = isPawn && piece.startsWith('w') && targetSquare[1] === '8';
        const isBlackPromotion = isPawn && piece.startsWith('b') && targetSquare[1] === '1';

        // For promotions, store the squares and return false so react-chessboard shows its dialog
        // The actual move will be handled by onPromotionPieceSelect
        if (isWhitePromotion || isBlackPromotion) {
            setPendingPromotion({ from: sourceSquare, to: targetSquare });
            return false;
        }

        onMove(sourceSquare, targetSquare);
        return true;
    };

    // Handle promotion piece selection from react-chessboard dialog
    const handlePromotionPieceSelect = (
        piece?: string,
        promoteFromSquare?: Square,
        promoteToSquare?: Square
    ): boolean => {
        if (disabled) return false;

        // Use passed squares first, fall back to our stored pending promotion
        const fromSquare = promoteFromSquare ?? pendingPromotion?.from;
        const toSquare = promoteToSquare ?? pendingPromotion?.to;

        // Clear pending promotion state
        setPendingPromotion(null);

        // If no piece selected (dialog cancelled) or no squares, return false
        if (!piece || !fromSquare || !toSquare) return false;

        // Extract promotion piece letter from piece code (e.g., 'wQ' -> 'q')
        const promotionPiece = piece[1]?.toLowerCase();
        if (!promotionPiece) return false;

        onMove(fromSquare, toSquare, promotionPiece);
        return true;
    };

    // Handle square click for click-to-move
    const onSquareClick = (square: Square) => {
        // If onSquareTap is provided, call it for any square click (tap_square/place_piece)
        if (onSquareTap) {
            onSquareTap(square);
            return;
        }

        if (disabled) return;

        // If we have a selected square, try to move there
        if (selectedSquare) {
            const moves = legalMoves?.get(selectedSquare);
            // Find a matching move - moves can be "e4" or "e8q" (with promotion)
            const matchingMove = moves?.find(m => m.startsWith(square));
            if (matchingMove) {
                const targetSquare = matchingMove.slice(0, 2);
                // Promotion suffix is the 3rd character if present (e.g., "e8q" -> "q")
                const promotion = matchingMove.length > 2 ? matchingMove[2] : undefined;

                onMove(selectedSquare, targetSquare, promotion);
                setSelectedSquare(null);
                return;
            }
        }

        // Check if clicking on a piece we can move
        const moves = legalMoves?.get(square);
        if (moves && moves.length > 0) {
            // Toggle selection: if clicking same square, deselect
            setSelectedSquare(selectedSquare === square ? null : square);
        } else {
            // Clicking on empty or opponent piece - deselect
            setSelectedSquare(null);
        }
    };

    // Custom square styles for highlighting
    const customSquareStyles: Record<string, React.CSSProperties> = {};

    // Detect check and find king position
    const checkInfo = useMemo(() => {
        if (!fen) return null;
        const engine = createEngine(fen);
        if (!engine.isCheck()) return null;

        // Find the king that's in check (current turn's king)
        const turn = engine.turn();
        const kingPiece = turn === 'w' ? 'K' : 'k';

        // Parse FEN to find king position
        const boardPart = fen.split(' ')[0];
        if (!boardPart) return null;

        const ranks = boardPart.split('/');
        for (let rankIdx = 0; rankIdx < 8; rankIdx++) {
            const rank = ranks[rankIdx];
            if (!rank) continue;
            let fileIdx = 0;
            for (const char of rank) {
                if (char >= '1' && char <= '8') {
                    fileIdx += parseInt(char);
                } else {
                    if (char === kingPiece) {
                        const file = String.fromCharCode(97 + fileIdx); // 'a' - 'h'
                        const rankNum = 8 - rankIdx; // 8 - 1
                        return `${file}${rankNum}`;
                    }
                    fileIdx++;
                }
            }
        }
        return null;
    }, [fen]);

    // Highlight king in check with orange tint
    if (checkInfo) {
        customSquareStyles[checkInfo] = {
            ...customSquareStyles[checkInfo],
            background: 'var(--color-check)',
        };
    }

    // Highlight last move
    if (lastMove) {
        const lastMoveColor = annotationTone === 'subtle'
            ? 'var(--color-board-highlight-subtle)'
            : 'var(--color-board-highlight)';
        customSquareStyles[lastMove.from] = {
            background: lastMoveColor,
        };
        customSquareStyles[lastMove.to] = {
            background: lastMoveColor,
        };
    }

    // Instructional highlight squares
    if (highlightSquares && highlightSquares.length > 0) {
        const highlightColor = annotationTone === 'subtle'
            ? 'var(--color-board-selected-subtle)'
            : 'var(--color-board-selected)';
        const ringWidth = annotationTone === 'subtle' ? '1.5px' : '2px';
        for (const square of highlightSquares) {
            customSquareStyles[square] = {
                ...customSquareStyles[square],
                boxShadow: `inset 0 0 0 ${ringWidth} ${highlightColor}`,
            };
        }
    }

    // Highlight selected square
    if (selectedSquare) {
        customSquareStyles[selectedSquare] = {
            ...customSquareStyles[selectedSquare],
            background: 'var(--color-board-selected)',
        };

        // Show legal move dots
        const moves = legalMoves?.get(selectedSquare);
        if (moves) {
            // Use Set to deduplicate (e.g., e8q, e8r, e8b, e8n all go to e8)
            const targetSquares = new Set(moves.map(m => m.slice(0, 2)));
            for (const targetSquare of targetSquares) {
                const existingBg = customSquareStyles[targetSquare]?.background || 'transparent';
                customSquareStyles[targetSquare] = {
                    ...customSquareStyles[targetSquare],
                    background: `${existingBg} radial-gradient(circle, var(--color-move-dot) 12%, transparent 13%)`,
                };
            }
        }
    }

    // Temporary web piece set. Replace with bundled original assets before final polish.
    const customPieces = useMemo(() => {
        const pieces = ['wP', 'wN', 'wB', 'wR', 'wQ', 'wK', 'bP', 'bN', 'bB', 'bR', 'bQ', 'bK'];
        const pieceComponents: Record<string, (args: any) => JSX.Element> = {};

        pieces.forEach(piece => {
            pieceComponents[piece] = ({ squareWidth }) => (
                <div
                    style={{
                        width: squareWidth,
                        height: squareWidth,
                        backgroundImage: `url(https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/mpchess/${piece}.svg)`,
                        backgroundSize: '100%',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                    }}
                />
            );
        });
        return pieceComponents;
    }, []);

    return (
        <div
            className={`board-container ${disabled ? 'board-disabled' : ''}`}
            data-piece-set="mpchess"
        >
            <Chessboard
                position={fen}
                boardOrientation={orientation}
                onPieceDrop={onDrop}
                onSquareClick={onSquareClick}
                onPromotionPieceSelect={handlePromotionPieceSelect}
                isDraggablePiece={isDraggablePiece}
                customSquareStyles={customSquareStyles}
                customBoardStyle={{
                    borderRadius: '0',
                }}
                customDarkSquareStyle={{ backgroundColor: 'var(--color-board-dark)' }}
                customLightSquareStyle={{ backgroundColor: 'var(--color-board-light)' }}
                areArrowsAllowed={false}
                showBoardNotation={true}
                animationDuration={200}
                customPieces={customPieces}
            />
            <ArrowsLayer arrows={arrows} orientation={orientation} tone={annotationTone} />
        </div>
    );
}
